import _ from 'lodash';
import React from 'react';
import { withWixSdk, Experiments } from '@wix/yoshi-flow-editor';
// eslint-disable-next-line import/no-extraneous-dependencies
import Raven from 'raven-js';
import { createWorkerHandlerForIframe } from './createHandlerForIframe';
import { getWixSDK, initWixSDK } from '../utils/wix-sdk';
import { attempt } from '../utils/attempt';
import { getVisitorId } from '../bi/bi-vod';
import { iframeAppBiLoggerFactory } from '@wix/iframe-app-bi-logger';
import getQueryParams from '../utils/get-query-params';
import { getCompId } from '../utils/comp-id';
import { getPageId } from '../utils/page-id';
import { isTablet } from '../utils/device/screen';
import { createController } from '../controller';
import { VIEW_MODES } from '@wix/wix-vod-constants/common';
import { withHttpClient } from '../containers/withHttpClient';
import { ControllerStateContext } from '../containers/withHandlersAndState';

function biLoggerFactory(args) {
  const WixSDK = getWixSDK();
  return iframeAppBiLoggerFactory(WixSDK, args);
}

function getHostAndPath() {
  const protocol = location.protocol;
  const host = location.host;
  const pathName = location.pathname.replace(/[^/]+$/, '');
  return `${protocol}//${host}${pathName}`;
}

function getBoundingRect() {
  const WixSDK = getWixSDK();

  return WixSDK.getBoundingRectAndOffsets().then((rectAndOffsets) => {
    const offsets = rectAndOffsets.offsets;
    const rect = rectAndOffsets.rect;

    return {
      scroll: {
        x: offsets.x,
        y: offsets.y - rect.top,
      },
    };
  });
}

function getFormFactor() {
  const WixSDK = getWixSDK();

  if (isTablet()) {
    return 'tablet';
  }
  return WixSDK.Utils.getDeviceType();
}

async function createAppControllerConfigFromWixSDK() {
  const WixSDK = getWixSDK();

  const getCurrentPageId = async () => getPageId() || WixSDK.getCurrentPageId();

  const getStyleParams = async () =>
    window.__STYLE_PARAMS__ || WixSDK.Styles.getStyleParams();

  const getCurrentSiteUser = async () =>
    window.__CURRENT_SITE_USER__ || WixSDK.currentMember();

  const [siteInfo, pageId, styleParams, currentSiteUser] = await Promise.all([
    new Promise(WixSDK.getSiteInfo),
    getCurrentPageId(),
    getStyleParams(),
    getCurrentSiteUser(),
  ]);

  const multilingualLang = getQueryParams(siteInfo.url).lang;
  const metaSiteId = WixSDK.Utils.getInstanceValue('metaSiteId');
  const viewMode = WixSDK.Utils.getViewMode();

  let templateMetaSiteId = null;

  if (!metaSiteId && /render\/template/.test(siteInfo.url)) {
    templateMetaSiteId = getQueryParams(siteInfo.url).metaSiteId;
  }

  const query = getQueryParams(window.location.search);
  const language = query.locale || WixSDK.Utils.getLocale();

  const createExperiments = ({ scopes }) =>
    new Experiments({
      baseUrl: 'https://manage.wix.com',
      scopes,
    });
  return {
    wixCodeApi: {
      window: {
        viewMode,
        formFactor: getFormFactor(),
        openPopup: (path, { width, height, theme, position }) =>
          new Promise((resolve) => {
            WixSDK.openPopup(path, width, height, position, resolve, theme);
          }),
        scrollTo: WixSDK.scrollTo,
        getBoundingRect,
        rendering: { env: 'browser' },
        multilingual: {
          isEnabled: Boolean(multilingualLang),
          currentLanguage: multilingualLang,
        },
      },
      user: {
        currentUser: currentSiteUser && {
          id: currentSiteUser.id,
          loggedIn: Boolean(currentSiteUser),
          getEmail: async () => currentSiteUser.email,
        },
        onLogin: _.noop,
      },
      location: {
        baseUrl: siteInfo.baseUrl,
        url: siteInfo.url,
        query,
      },
      site: {
        language,
        currentPage: { id: pageId },
        onInstanceChanged: (cb) =>
          WixSDK.addEventListener(WixSDK.Events.INSTANCE_CHANGED, cb),
      },
    },
    platformAPIs: {
      biLoggerFactory,
      bi: {
        biToken: attempt(() => WixSDK.Utils.getInstanceValue('biToken')),
        ownerId: WixSDK.Utils.getInstanceValue('siteOwnerId'),
        metaSiteId,
        templateMetaSiteId,
        visitorId: getVisitorId(),
      },
      monitoring: { createMonitor: (url) => Raven.config(url) },
      pubSub: WixSDK.PubSub,
    },
    appParams: {
      instance: WixSDK.Utils.getInstance(),
      instanceId: WixSDK.Utils.getInstanceId(),
      baseUrls: {
        staticsBaseUrl: getHostAndPath(),
      },
    },
    config: {
      style: {
        styleParams: _.omit(styleParams, 'siteColors', 'textPresets'),
        siteColors: styleParams.siteColors || WixSDK.Styles.getSiteColors(),
        siteTextPresets:
          styleParams.textPresets || WixSDK.Styles.getSiteTextPresets(),
      },
    },
    compId: getCompId(),
    essentials: {
      experiments: createExperiments({ scopes: ['vod'] }),
      createExperiments,
    },
  };
}

function getPlatformComponentProps({ config }) {
  const WixSDK = getWixSDK();

  return {
    host: {
      registerToComponentDidLayout: (cb) => cb(),
      registerToScroll: (cb) =>
        WixSDK.addEventListener(WixSDK.Events.SCROLL, cb),
      style: config.style,
    },
  };
}

export function iframeWrapper(Component, { getStoreBuilder }, isMobile) {
  const controllerFunction = createController(
    createWorkerHandlerForIframe,
    getStoreBuilder,
    true,
  );

  class WrappedComponent extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        loaded: false,
        componentProps: {
          host: {
            style: {},
          },
          renderingEnv: 'browser',
        },
      };

      this.init().then();
    }

    setComponentProps = (componentProps) => {
      const platformComponentProps = getPlatformComponentProps(
        this.controllerConfig,
      );
      this.setState((currentState) => ({
        componentProps: {
          ...currentState.componentProps,
          ...componentProps,
          ...platformComponentProps,
        },
        loaded: true,
      }));
    };

    init = async () => {
      const {
        wixSDK: { Wix },
        httpClient,
      } = this.props;

      const viewMode = Wix.Utils.getViewMode();

      initWixSDK(Wix);
      this.controllerConfig = {
        ...(await createAppControllerConfigFromWixSDK()),
        setProps: this.setComponentProps,
      };

      const settings = await new Promise((resolve) => {
        Wix.Data.Public.getAll((data) =>
          resolve(data[Wix.Data.SCOPE.COMPONENT]),
        );
      });

      const appController = await controllerFunction({
        controllerConfig: this.controllerConfig,
        flowAPI: {
          httpClient,
          environment: {
            isSSR: false,
            isMobile,
            isViewer: viewMode === VIEW_MODES.SITE,
          },
          reportError: () => {
            // TODO: reportError for iframe
          },
          settings: {
            get: (param) => settings?.[param.key],
          },
        },
      });

      await appController.pageReady();
    };

    render() {
      const { componentProps, loaded } = this.state;

      if (loaded) {
        return (
          <ControllerStateContext.Provider
            value={{
              handlers: componentProps.handlers,
              controllerState: componentProps.controllerState,
            }}
          >
            <Component {...this.props} {...componentProps} />
          </ControllerStateContext.Provider>
        );
      }
      return null;
    }
  }

  return withHttpClient(withWixSdk(WrappedComponent));
}
